import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import {
  checkSiteAdminPermission,
  checkUserAccessPermission,
  checkHasAnyACPermission,
  checkHasEmailReachFullAccessPermission,
  checkHasAnySubscriptionPermission,
  checkHasAnySyndicationPermission,
  checkEditorialAccessPermission,
  checkHasAnyBroadcastPermission,
} from 'utility/permissions';
import { hasEnhancedAccess } from 'utility/privileges';

/**
 * Navigation Utility Functions
 */

/**
 * Maps application paths to their corresponding help documentation URLs
 * Returns the appropriate help documentation URL based on the current application path
 * @param pathname - The current application pathname (without the activeSite prefix)
 * @returns The corresponding help documentation URL
 */
export const getHelpUrl = (pathname: string): string => {
  // Strip leading and trailing slashes for consistent matching
  const cleanPath = pathname.replace(/^\/|\/$/g, '');

  // Base help URL
  const baseHelpUrl = 'https://help.bloxdigital.com/nxt_help';

  // Special case for root path
  if (cleanPath === '') {
    return baseHelpUrl + '/';
  }

  // For edit paths, only keep up to /edit/ part
  if (cleanPath.includes('/edit/')) {
    const editBasePath = cleanPath.split('/edit/')[0] + '/edit';
    return `${baseHelpUrl}/${editBasePath}/`;
  }

  // For view paths, only keep up to /view/ part
  if (cleanPath.includes('/view/')) {
    const viewBasePath = cleanPath.split('/view/')[0] + '/view';
    return `${baseHelpUrl}/${viewBasePath}/`;
  }

  // For all other paths, use the application path directly
  return `${baseHelpUrl}/${cleanPath}/`;
};

/**
 * GetCommunityAccessDetails
 * This function is used in both AppsBento and LeftNav.
 *
 * Community holds a handful of routes cutting across user, subscription,
 * access control, email reach, etc. We will need a more complex check to
 * determine if hiding the bento item or not. It should mirror the same checks
 * as the LeftNav.tsx does, as long as the user has at least one of these
 * permissions, we should show the community bento item.
 *
 * So if customer has at least one of these permissions, we can show community.
 * We have left the route as `/user/dashboard` as default, since there was
 * unsettled discussion about this route a user preference in future, even if
 * end user may not neccessarily have access 'user/dashboard' app -- because they
 * need to be able get access to the app they do have access to within community.
 */

type GetCommunityAccessDetailsParams = {
  sessionConfig?: TSessionConfigData;
};

type GetCommunityAccessDetailsResult = {
  isAdmin: boolean; // If the user is a site admin
  hasContacts: boolean; // compound checks, admin or has user app access
  hasUserMultiSearch: boolean; // If the user has multisite user search.
  hasJourneys: boolean;
  hasEmailReach: boolean;
  hasServices: boolean;
  hasTransactions: boolean;
  hasReports: boolean;
  hasCommunity: boolean; // for apps bento, has any of the above
};

export function getCommunityAccessDetails({
  sessionConfig,
}: GetCommunityAccessDetailsParams): GetCommunityAccessDetailsResult {
  // Only show multisite search if user has enhanced access privilege.
  const hasEnhanced = hasEnhancedAccess(sessionConfig);

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasUserAccess } = checkUserAccessPermission(sessionConfig);
  const { granted: userHasAccessControl } = checkHasAnyACPermission(sessionConfig);
  const { granted: userHasEmailReach } =
    checkHasEmailReachFullAccessPermission(sessionConfig);
  const { granted: userHasSubscription } =
    checkHasAnySubscriptionPermission(sessionConfig);

  const hasContacts = Boolean(userIsAdmin || userHasUserAccess);
  const hasServices = Boolean(userIsAdmin || userHasSubscription);
  const hasTransactions = hasServices;
  const hasJourneys = Boolean(userIsAdmin || userHasAccessControl);
  const hasEmailReach = Boolean(userIsAdmin || userHasEmailReach);
  const hasReports = Boolean(userIsAdmin || userHasSubscription);

  const hasUserMultiSearch = Boolean(hasContacts && hasEnhanced);

  const hasCommunity = Boolean(
    userIsAdmin ||
      hasContacts ||
      hasJourneys ||
      hasEmailReach ||
      hasServices ||
      hasTransactions ||
      hasReports,
  );

  const result: GetCommunityAccessDetailsResult = {
    isAdmin: userIsAdmin,
    hasContacts: hasContacts,
    hasUserMultiSearch: hasUserMultiSearch,
    hasJourneys: hasJourneys,
    hasEmailReach: hasEmailReach,
    hasServices: hasServices,
    hasTransactions: hasTransactions,
    hasReports: hasReports,
    hasCommunity: hasCommunity,
  };

  return result;
}

/**
 * Similar issue of permissions mixing cross applications in Content
 * as above in Community. This is used in AppsBento to determine if
 * to show "Content" Management apps bento item, does the user have
 * access to at least one subapplication of content management?
 */

type GetContentMgmtAccessDetailsArgs = {
  sessionConfig?: TSessionConfigData;
};

type GetContentMgmtAccessDetailsResult = {
  isAdmin: boolean; // If the user is a site admin
  hasEditorial: boolean;
  hasSyndication: boolean; // wire
  hasNotifier: boolean; // broadcast
  hasContentMgmt: boolean; // for apps bento, has any of the above
};

export function getContentMgmtAccessDetails({
  sessionConfig,
}: GetContentMgmtAccessDetailsArgs): GetContentMgmtAccessDetailsResult {
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasEditorial } = checkEditorialAccessPermission(sessionConfig);
  const { granted: userHasSyndication } = checkHasAnySyndicationPermission(sessionConfig);
  const { granted: userHasNotifier } = checkHasAnyBroadcastPermission(sessionConfig);

  const hasSyndication = Boolean(userIsAdmin || userHasSyndication);
  const hasEditorial = Boolean(userIsAdmin || userHasEditorial);
  const hasNotifier = Boolean(userIsAdmin || userHasNotifier);

  // Admin or has at least one content mgmt app
  const hasContentMgmt = Boolean(
    userIsAdmin || hasEditorial || hasSyndication || hasNotifier,
  );

  const result: GetContentMgmtAccessDetailsResult = {
    isAdmin: userIsAdmin,
    hasEditorial: hasEditorial,
    hasSyndication: hasSyndication,
    hasNotifier: hasNotifier,
    hasContentMgmt: hasContentMgmt,
  };

  return result;
}
